import React from 'react';
import { useTranslation } from 'react-i18next';
import { Youtube, Tag, Eye } from 'lucide-react';

interface YouTubePostSettingsCardProps {
  title: string;
  tags: string[];
  privacy: 'public' | 'private' | 'unlisted';
  onTitleChange: (title: string) => void;
  onTagsChange: (tags: string[]) => void;
  onPrivacyChange: (privacy: 'public' | 'private' | 'unlisted') => void;
}

export const YouTubePostSettingsCard: React.FC<YouTubePostSettingsCardProps> = ({
  title,
  tags,
  privacy,
  onTitleChange,
  onTagsChange,
  onPrivacyChange
}) => {
  const { t } = useTranslation();
  const [tagInput, setTagInput] = React.useState('');

  const handleAddTag = () => {
    if (tagInput.trim()) {
      onTagsChange([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    onTagsChange(newTags);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-6">
        <Youtube className="w-5 h-5 text-red-600" />
        <h2 className="text-lg font-semibold text-gray-900">YouTube Settings</h2>
      </div>

      <div className="space-y-4">
        {/* Video Title */}
        <div>
          <label htmlFor="youtubeTitle" className="block text-sm font-medium text-gray-700 mb-1">
            Video Title
          </label>
          <input
            type="text"
            id="youtubeTitle"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Enter video title (leave empty to use post content)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Tags */}
        <div>
          <label htmlFor="youtubeTags" className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="youtubeTags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add tags"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              onClick={handleAddTag}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Add
            </button>
          </div>
          {tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 px-2 py-1 bg-primary-100 rounded-full"
                >
                  <Tag className="w-3 h-3 text-primary-700" />
                  <span className="text-sm text-primary-700">{tag}</span>
                  <button
                    onClick={() => handleRemoveTag(index)}
                    className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Privacy */}
        <div>
          <label htmlFor="youtubePrivacy" className="block text-sm font-medium text-gray-700 mb-1">
            Privacy
          </label>
          <select
            id="youtubePrivacy"
            value={privacy}
            onChange={(e) => onPrivacyChange(e.target.value as 'public' | 'private' | 'unlisted')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="public">Public</option>
            <option value="unlisted">Unlisted</option>
            <option value="private">Private</option>
          </select>
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
            <Eye className="w-4 h-4" />
            <span>
              {privacy === 'public'
                ? 'Anyone can search for and view'
                : privacy === 'unlisted'
                ? 'Only people with the link can view'
                : 'Only you can view'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};